<template>
    <div class="library-deliverable-table-metadata-header-cell">
        <EditMetadataFieldModal
            v-if="editMetadataFieldModalIsVisible"
            :column-name="columnName"
            :field-id="fieldId"
            @close="toggleEditMetadataFieldModalIsVisible"
        />
        <SetMetadataValuesModal
            v-if="setMetadataValuesModalIsVisible"
            :metadata-field-id="fieldId"
            :deliverables="deliverables"
            :selected-deliverables="selectedDeliverables"
            :all-selected-mode="allSelectedMode"
            :total-items="totalItems"
            @close="toggleSetMetadataValuesModalIsVisible"
            @metadataValueUpdatedOnDeliverables="$emit('metadataValueUpdatedOnDeliverables', $event)"
        />
        <div class="library-deliverable-table-metadata-header-cell__content">
            <span class="library-deliverable-table-metadata-header-cell__title">
                <slot />
            </span>
            <div
                v-if="userCanManageMetadata"
                class="library-deliverable-table-metadata-header-cell__edit-button-wrapper"
            >
                <Dropdown
                    placement="bottom-start"
                    transfer
                    trigger="custom"
                    :visible="dropdownIsVisible"
                    @onClickoutside="toggleDropdownIsVisible"
                >
                    <hox-square-button
                        has-inverse-hover
                        :is-active="dropdownIsVisible"
                        size="small"
                        type="primary"
                        @click="toggleDropdownIsVisible"
                    >
                        <Icon type="ios-more" />
                    </hox-square-button>
                    <template #list>
                        <DropdownItem @click="toggleEditMetadataFieldModalIsVisible">Edit column</DropdownItem>
                        <DropdownItem @click="toggleSetMetadataValuesModalIsVisible">Set values</DropdownItem>
                    </template>
                </Dropdown>
            </div>
        </div>
    </div>
</template>

<script lang="ts">
import { defineComponent, type PropType } from "vue";

import EditMetadataFieldModal from "@/components/Metadata/EditMetadataFieldModal.vue";
import SetMetadataValuesModal from "@/components/Metadata/SetMetadataValuesModal.vue";
import type { ConstructedDeliverable } from "@/types/types";

export default defineComponent({
    components: {
        EditMetadataFieldModal,
        SetMetadataValuesModal
    },

    props: {
        allSelectedMode: {
            type: Boolean,
            default: false
        },

        columnName: {
            required: true,
            type: String
        },

        deliverables: {
            required: true,
            type: Array as PropType<ConstructedDeliverable[]>
        },

        fieldId: {
            type: String,
            required: true
        },

        selectedDeliverables: {
            required: true,
            type: Array as PropType<ConstructedDeliverable[]>
        },

        totalItems: {
            required: true,
            type: Number
        }
    },

    emits: ["metadataValueUpdatedOnDeliverables"],

    data() {
        return {
            dropdownIsVisible: false,
            editMetadataFieldModalIsVisible: false,
            setMetadataValuesModalIsVisible: false
        };
    },

    computed: {
        campaignId() {
            return this.$route.params.campaignId as string;
        },

        clientId() {
            return this.$route.params.clientId as string;
        },

        userCanManageMetadata() {
            return this.$auth.userCan(this.$auth.Actions.CanManageMetadata, {
                clientId: this.clientId,
                campaignId: this.campaignId
            });
        }
    },

    methods: {
        toggleDropdownIsVisible() {
            this.dropdownIsVisible = !this.dropdownIsVisible;
        },

        toggleEditMetadataFieldModalIsVisible() {
            if (!this.editMetadataFieldModalIsVisible) {
                this.dropdownIsVisible = false;
            }

            this.editMetadataFieldModalIsVisible = !this.editMetadataFieldModalIsVisible;
        },

        toggleSetMetadataValuesModalIsVisible() {
            if (!this.editMetadataFieldModalIsVisible) {
                this.dropdownIsVisible = false;
            }

            this.setMetadataValuesModalIsVisible = !this.setMetadataValuesModalIsVisible;
        }
    }
});
</script>

<style lang="scss">
@use "@style/variables" as *;
.library-deliverable-table-metadata-header-cell {
    font-weight: bold;
    width: 100%;
}

.library-deliverable-table-metadata-header-cell__content {
    align-items: center;
    display: flex;
    justify-content: space-between;
    width: 100%;
}

.library-deliverable-table-metadata-header-cell__edit-button-wrapper {
    margin: (0 - $spacing-semi-small) 0 (0 - $spacing-semi-small) $spacing-smaller;
}

.library-deliverable-table-metadata-header-cell__title {
    flex: 1;
    overflow: hidden;
    text-overflow: ellipsis;
}
</style>
